import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { ConsultationDTO } from "@arna-med/shared";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/ui/pagination";
import { ConfirmDialog } from "@/components/ConfirmDialog";

interface ConsultationListProps {
  consultations: ConsultationDTO[];
  isLoading: boolean;
  page: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  emptyMessage?: string;
  showPatient?: boolean;
  onDelete?: (id: string) => void;
  isDeleting?: boolean;
}

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy} / ${hh}:${min}`;
}

export function ConsultationList({
  consultations,
  isLoading,
  page,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  emptyMessage,
  showPatient = true,
  onDelete,
  isDeleting,
}: ConsultationListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  if (isLoading) {
    return <Spinner />;
  }

  if (consultations.length === 0) {
    return (
      <p className="py-12 text-center text-muted-foreground">
        {emptyMessage ?? t("dashboard.noConsultations")}
      </p>
    );
  }

  return (
    <>
      <div className="rounded-lg border overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="border-b bg-muted/50">
              {showPatient && (
                <>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground whitespace-nowrap">
                    {t("dashboard.colFullName")}
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground whitespace-nowrap">
                    {t("dashboard.colIin")}
                  </th>
                </>
              )}
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground whitespace-nowrap">
                {t("dashboard.colDateTime")}
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground whitespace-nowrap">
                {t("dashboard.colDoctor")}
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground whitespace-nowrap">
                {t("dashboard.colStatus")}
              </th>
              {onDelete && <th className="px-4 py-3" />}
            </tr>
          </thead>
          <tbody>
            {consultations.map((c) => (
              <tr
                key={c.id}
                className="border-b last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
                onClick={() => navigate(`/consultation/${c.id}`)}
              >
                {showPatient && (
                  <>
                    <td className="px-4 py-3 font-medium whitespace-nowrap">
                      {c.patientName}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                      {c.patientPersonalNumber ?? "—"}
                    </td>
                  </>
                )}
                <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                  {formatDateTime(c.startedAt)}
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {c.doctorName ?? "—"}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-1.5">
                    {c.flaggedForAudit && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                        title={t("dashboard.flaggedForAudit")}
                      >
                        ⚠️ {t("dashboard.flagged")}
                      </span>
                    )}
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        c.status === "active"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {t(`dashboard.status.${c.status}`)}
                    </span>
                  </div>
                </td>
                {onDelete && (
                  <td
                    className="px-4 py-3 whitespace-nowrap"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setConfirmDeleteId(c.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={onPageChange}
      />
      {onDelete && (
        <ConfirmDialog
          open={confirmDeleteId !== null}
          onOpenChange={(open) => {
            if (!open) setConfirmDeleteId(null);
          }}
          onConfirm={() => {
            onDelete(confirmDeleteId!);
            setConfirmDeleteId(null);
          }}
          isPending={isDeleting}
        />
      )}
    </>
  );
}
